import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface RankReward {
  text: string;
  image_url: string | null;
}

interface Rank {
  id: number;
  title: string;
  slug: string;
  image_url: string | null;
  points_required: number;
  reward: RankReward;
}

const RankRewards = () => {
  const [ranks, setRanks] = useState<Rank[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRanks();
  }, []);

  const fetchRanks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the same base URL pattern as other API calls
      const baseUrl = process.env.REACT_APP_WORDPRESS_URL || process.env.REACT_APP_WORDPRESS_API_URL || 'https://oracle-gaming.co.za';
      // Ensure URL has protocol
      const apiBase = baseUrl.startsWith('http') ? baseUrl : `https://${baseUrl}`;
      // Remove trailing slash if present
      const cleanBase = apiBase.replace(/\/$/, '');
      const apiUrl = `${cleanBase}/wp-json/oracle/v1/gamification/ranks`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ranks: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setRanks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching ranks:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Rank Rewards - Oracle Gaming Pokemon & One Piece TCG Store South Africa</title>
        <meta name="description" content="View all available ranks and their exclusive rewards at Oracle Gaming. Earn XP through purchases and unlock amazing rewards!" />
        <meta name="keywords" content="rank rewards, gamification, Oracle Gaming rewards, XP rewards, TCG rewards South Africa" />
        <meta property="og:title" content="Rank Rewards - Oracle Gaming" />
        <meta property="og:description" content="View all available ranks and their exclusive rewards at Oracle Gaming." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://oracle-gaming.vercel.app/rank-rewards" />
        <link rel="canonical" href="https://oracle-gaming.vercel.app/rank-rewards" />
      </Helmet>
      <Header />
      <main style={{ 
        backgroundColor: '#1a1b20', 
        minHeight: '100vh',
        margin: 0,
        paddingTop: '120px',
        paddingBottom: '60px'
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 15px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h1 className="h1 section-title" style={{ 
              marginBottom: '20px',
              fontSize: 'var(--fs-1)',
              color: 'var(--white)'
            }}>
              Rank Rewards
            </h1>
            <p style={{ 
              color: 'var(--platinum)', 
              fontSize: 'var(--fs-7)',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Earn XP with every purchase and unlock exclusive rewards as you rank up!
            </p>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p style={{ color: 'var(--white)', fontSize: 'var(--fs-6)' }}>Loading ranks...</p>
            </div>
          )}

          {error && (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <p style={{ color: '#ef4444', fontSize: 'var(--fs-6)' }}>
                {error}
              </p>
            </div>
          )}

          {!loading && !error && ranks.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <p style={{ color: 'var(--white)', fontSize: 'var(--fs-6)' }}>
                No ranks available at this time.
              </p>
            </div>
          )}

          {!loading && !error && ranks.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '30px',
              marginTop: '40px'
            }}>
              {ranks.map((rank) => (
                <div
                  key={rank.id}
                  style={{
                    background: 'var(--raisin-black-3)',
                    border: '2px solid rgba(20, 254, 251, 0.2)',
                    borderRadius: '8px',
                    padding: '24px',
                    transition: 'var(--transition-1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--orange)';
                    e.currentTarget.style.transform = 'translateY(-5px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(20, 254, 251, 0.2)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {/* Rank Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '20px',
                    paddingBottom: '16px',
                    borderBottom: '1px solid rgba(20, 254, 251, 0.1)'
                  }}>
                    {rank.image_url ? (
                      <img
                        src={rank.image_url}
                        alt={rank.title}
                        style={{
                          width: '64px',
                          height: '64px',
                          minWidth: '64px',
                          minHeight: '64px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          border: '2px solid var(--orange)',
                          flexShrink: 0
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '64px',
                        height: '64px',
                        background: 'rgba(20, 254, 251, 0.1)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px',
                        border: '2px solid var(--orange)'
                      }}>
                        🏆
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        margin: 0,
                        color: 'var(--white)',
                        fontSize: 'var(--fs-5)',
                        fontFamily: 'var(--ff-oswald)',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>
                        {rank.title}
                      </h3>
                      {rank.points_required > 0 && (
                        <p style={{
                          margin: '4px 0 0 0',
                          color: 'var(--orange)',
                          fontSize: 'var(--fs-9)',
                          fontFamily: 'var(--ff-poppins)'
                        }}>
                          {rank.points_required.toLocaleString()} XP Required
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Reward Section */}
                  {rank.reward.text || rank.reward.image_url ? (
                    <div style={{ marginTop: '20px' }}>
                      {rank.reward.image_url && (
                        <div style={{
                          marginBottom: '16px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid rgba(20, 254, 251, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(20, 254, 251, 0.05)',
                          minHeight: '200px',
                          maxHeight: '400px'
                        }}>
                          <img
                            src={rank.reward.image_url}
                            alt={`${rank.title} Reward`}
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '400px',
                              objectFit: 'contain',
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      {rank.reward.text && (() => {
                        // Split text by " | " to separate reward text from additional info
                        const parts = rank.reward.text.split(' | ');
                        const rewardText = parts[0] || '';
                        const additionalInfo = parts[1] || '';
                        
                        return (
                          <div style={{
                            padding: '16px',
                            background: 'rgba(20, 254, 251, 0.05)',
                            borderRadius: '6px',
                            border: '1px solid rgba(20, 254, 251, 0.1)'
                          }}>
                            {rewardText && (
                              <p style={{
                                margin: 0,
                                marginBottom: additionalInfo ? '8px' : 0,
                                color: 'var(--platinum)',
                                fontSize: 'var(--fs-8)',
                                lineHeight: '1.6',
                                fontFamily: 'var(--ff-poppins)',
                                fontWeight: 'bold'
                              }}>
                                {rewardText}
                              </p>
                            )}
                            {additionalInfo && (
                              <p style={{
                                margin: 0,
                                color: 'var(--platinum)',
                                fontSize: 'var(--fs-8)',
                                lineHeight: '1.6',
                                fontFamily: 'var(--ff-poppins)',
                                opacity: 0.8
                              }}>
                                {additionalInfo}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: 'var(--light-gray)',
                      fontSize: 'var(--fs-9)',
                      fontStyle: 'italic'
                    }}>
                      Reward details coming soon
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default RankRewards;

